import type {
  ProjectMixProjectRow,
  ProjectMixStats,
} from '../types/projectAnalysis'
import { MONTH_NAMES } from './projectAnalysisPeriod'
import type { OverviewFilterState } from './projectOverviewLists'

export type DisplaySummaryRow = Record<string, number | string | null | undefined>

export function computePeriodSummary(
  projectMixStats: ProjectMixStats | null,
  orgPeriodKeys: string[] | null,
  s: OverviewFilterState,
): DisplaySummaryRow | null {
  if (!projectMixStats) return null
  const byYm = projectMixStats.by_year_month
  const byY = projectMixStats.by_year
  const byYmProj = projectMixStats.by_year_month_projects
  if (orgPeriodKeys && orgPeriodKeys.length > 0 && byYm) {
    let th = 0
    let bh = 0
    let nb = 0
    let entries = 0
    let late = 0
    let pending = 0
    let totalLeaveDays = 0
    const projectsSet = new Set<number>()
    orgPeriodKeys.forEach((key) => {
      const period = byYm[key]
      if (!period?.summary) return
      const sum = period.summary
      th += sum.total_hours || 0
      bh += sum.billable_hours || 0
      nb += sum.non_billable_hours || 0
      entries += sum.total_entries || 0
      late += sum.late_entries || 0
      pending += sum.pending_signoffs || 0
      totalLeaveDays += sum.total_leave_days || 0
      if (byYmProj && Array.isArray(byYmProj[key])) {
        byYmProj[key].forEach((p) => projectsSet.add(p.project_id))
      } else {
        ;(period.top_projects_by_billable || []).forEach((p) =>
          projectsSet.add(p.project_id),
        )
      }
    })
    const lateRate =
      entries > 0 ? Math.round((late / entries) * 1000) / 10 : null
    return {
      total_hours: Math.round(th * 100) / 100,
      billable_hours: Math.round(bh * 100) / 100,
      non_billable_hours: Math.round(nb * 100) / 100,
      projects_count: projectsSet.size,
      total_entries: entries,
      late_entries: late,
      pending_signoffs: pending,
      total_leave_days: Math.round(totalLeaveDays * 10) / 10,
      late_entry_rate: lateRate,
      subtitle:
        s.orgFilterMode === 'fy'
          ? `FY ${s.orgFY}${s.orgQ && s.orgQ !== 'all' ? ` ${s.orgQ}` : ''}`
          : s.orgMonth && s.orgMonth !== 'all'
            ? `${MONTH_NAMES[Number(s.orgMonth) - 1]} ${s.orgYear}`
            : `Calendar ${s.orgYear}`,
    }
  }
  if (
    s.orgFilterMode === 'calendar' &&
    s.orgYear &&
    s.orgYear !== 'all' &&
    !orgPeriodKeys?.length &&
    byY
  ) {
    const y = String(s.orgYear)
    const period = byY[y]
    if (!period?.summary) return projectMixStats.summary as DisplaySummaryRow
    const sum = period.summary
    return {
      ...sum,
      total_leave_days: sum.total_leave_days ?? 0,
      subtitle: `Calendar ${s.orgYear}`,
    }
  }
  if (projectMixStats.summary) {
    return {
      ...projectMixStats.summary,
      total_leave_days: projectMixStats.summary.total_leave_days ?? 0,
      subtitle: 'All time',
    } as DisplaySummaryRow
  }
  return null
}

export function computeCategorySummary(
  orgCategoryFilter: string,
  categoryFilteredProjectList: ProjectMixProjectRow[],
): DisplaySummaryRow | null {
  if (!orgCategoryFilter) return null
  const list = categoryFilteredProjectList
  const total_hours = list.reduce((s, p) => s + (Number(p.total_hours) || 0), 0)
  const billable_hours = list.reduce(
    (s, p) => s + (Number(p.billable_hours) || 0),
    0,
  )
  const non_billable_hours = list.reduce(
    (s, p) => s + (Number(p.non_billable_hours) || 0),
    0,
  )
  const total_entries = list.reduce(
    (s, p) => s + (Number(p.entries_count) || 0),
    0,
  )
  const late_entries = list.reduce(
    (s, p) => s + (Number(p.late_entries) || 0),
    0,
  )
  const pending_signoffs = list.reduce(
    (s, p) => s + (Number(p.pending_signoffs) || 0),
    0,
  )
  const total_leave_days = list.reduce(
    (s, p) => s + (Number(p.leave_days) || 0),
    0,
  )
  const categoryLabel =
    orgCategoryFilter.charAt(0).toUpperCase() + orgCategoryFilter.slice(1)
  const subtitle =
    list.length === 0
      ? `All time · ${categoryLabel} (no projects)`
      : `All time · ${categoryLabel} (${list.length} project${list.length === 1 ? '' : 's'})`
  return {
    total_hours: Math.round(total_hours * 100) / 100,
    billable_hours: Math.round(billable_hours * 100) / 100,
    non_billable_hours: Math.round(non_billable_hours * 100) / 100,
    total_entries,
    late_entries,
    pending_signoffs,
    total_leave_days: Math.round(total_leave_days * 10) / 10,
    late_entry_rate:
      total_entries > 0
        ? Math.round((late_entries / total_entries) * 1000) / 10
        : null,
    projects_count: list.length,
    subtitle,
  }
}

export function computePeriodCategorySummary(
  projectMixStats: ProjectMixStats | null,
  orgPeriodKeys: string[] | null,
  orgCategoryFilter: string,
  s: OverviewFilterState,
): DisplaySummaryRow | null {
  if (!orgCategoryFilter || !orgPeriodKeys?.length || !projectMixStats)
    return null
  const byYmCat = projectMixStats.by_year_month_by_category
  const byYCat = projectMixStats.by_year_by_category
  const byYmProj = projectMixStats.by_year_month_projects
  const catKey = orgCategoryFilter.toLowerCase()
  let th = 0
  let bh = 0
  let nb = 0
  let entries = 0
  let late = 0
  let pending = 0
  let totalLeaveDays = 0
  const uniqueProjectIds = new Set<number>()

  if (byYmCat && typeof byYmCat === 'object') {
    orgPeriodKeys.forEach((key) => {
      const periodCat = byYmCat[key]
      const row = periodCat && periodCat[catKey]
      if (!row) return
      th += row.total_hours || 0
      bh += row.billable_hours || 0
      nb += row.non_billable_hours || 0
      entries += row.total_entries || 0
      late += row.late_entries || 0
      pending += row.pending_signoffs || 0
      totalLeaveDays += row.total_leave_days || 0
    })
  }
  if (
    th === 0 &&
    entries === 0 &&
    byYCat &&
    s.orgFilterMode === 'calendar' &&
    s.orgYear &&
    s.orgYear !== 'all' &&
    s.orgMonth === 'all'
  ) {
    const yCat = byYCat[String(s.orgYear)]
    const sc = yCat && yCat[catKey]
    if (sc) {
      th = sc.total_hours || 0
      bh = sc.billable_hours || 0
      nb = sc.non_billable_hours || 0
      entries = sc.total_entries || 0
      late = sc.late_entries || 0
      pending = sc.pending_signoffs || 0
      totalLeaveDays = sc.total_leave_days || 0
    }
  }
  if (byYmProj && typeof byYmProj === 'object') {
    orgPeriodKeys.forEach((key) => {
      const arr = byYmProj[key]
      if (!Array.isArray(arr)) return
      arr.forEach((p) => {
        const pCat = (p.project_category || '').toString().toLowerCase()
        if (pCat === catKey) uniqueProjectIds.add(p.project_id)
      })
    })
  }
  const lateRate =
    entries > 0 ? Math.round((late / entries) * 1000) / 10 : null
  const categoryLabel =
    orgCategoryFilter.charAt(0).toUpperCase() + orgCategoryFilter.slice(1)
  const periodLabel =
    s.orgFilterMode === 'fy'
      ? `FY ${s.orgFY}${s.orgQ && s.orgQ !== 'all' ? ` ${s.orgQ}` : ''}`
      : s.orgMonth && s.orgMonth !== 'all'
        ? `${MONTH_NAMES[Number(s.orgMonth) - 1]} ${s.orgYear}`
        : `Calendar ${s.orgYear}`
  const subtitle = `${periodLabel} · ${categoryLabel}`
  let projects_count = uniqueProjectIds.size
  if (projects_count === 0 && byYmCat) {
    projects_count = orgPeriodKeys.reduce((acc, key) => {
      const row = byYmCat[key] && byYmCat[key][catKey]
      return acc + (row && row.projects_count != null ? row.projects_count : 0)
    }, 0)
  }
  return {
    total_hours: Math.round(th * 100) / 100,
    billable_hours: Math.round(bh * 100) / 100,
    non_billable_hours: Math.round(nb * 100) / 100,
    total_entries: entries,
    late_entries: late,
    pending_signoffs: pending,
    total_leave_days: Math.round(totalLeaveDays * 10) / 10,
    late_entry_rate: lateRate,
    projects_count,
    subtitle,
  }
}

export function mergeDisplaySummary(
  orgCategoryFilter: string,
  orgPeriodKeys: string[] | null,
  periodCategorySummary: DisplaySummaryRow | null,
  categorySummary: DisplaySummaryRow | null,
  periodSummary: DisplaySummaryRow | null,
  summary: ProjectMixStats['summary'] | undefined,
): DisplaySummaryRow | null {
  const ds = orgCategoryFilter
    ? orgPeriodKeys?.length && periodCategorySummary
      ? periodCategorySummary
      : categorySummary
    : periodSummary || summary
  if (!ds) return null
  const leaveDays =
    (ds.total_leave_days as number | undefined) ??
    (summary?.total_leave_days ?? 0)
  return { ...ds, total_leave_days: leaveDays }
}
